import h from 'hyperscript';

import { AFTER_LOAD_DATA } from 'ui/data-loading-event-types';

import { OlapPivotTablePlugin } from 'cadenza/result-table/olap-pivot-table-plugin';
import { getDataTableElement, setDataTableMarginTop } from 'cadenza/result-table/result-table-utils';
import type { EventKey } from 'cadenza/utils/event-util';
import { on, unByKey } from 'cadenza/utils/event-util';
import type { OlapPivotTable } from 'cadenza/result-table/olap-pivot-table';
import type { TableConfigurationDto } from 'cadenza/result-table/table-configuration-dto';
import type { TableRowDto } from 'cadenza/ui/base-table/model/table-row-dto';
import { isChromiumBrowser, isSafari } from 'cadenza/utils/browser';
import { px } from 'cadenza/utils/px';
import { DATATABLE_DESTROYED } from 'cadenza/ui/base-table/base-table-event-types';
import type { BaseTableRowElement } from 'cadenza/ui/base-table/base-table';

import type { Api } from 'datatables.net';

import './row-span-olap-pivot-table-plugin.css';

const COMPONENT_NAME = 'd-row-span-olap-pivot-table-plugin';

/**
 * Determines the equality of cells values from different rows of the same column.
 * @param rowA Row a hosting the cell to compare
 * @param rowB Row b hosting the cell to compare
 * @param columnIndex The column index of the cell to compare
 */
const CELL_VALUE_COMPARE_FUNCTION = (rowA: TableRowDto, rowB: TableRowDto, columnIndex: number) =>
  rowA.values[columnIndex] === rowB.values[columnIndex];

/**
 * Defines a cell group that will span across cells of connected rows where the values of the columns
 * identified by columnIndex and relatedColumnIndices are equal.
 */
interface ColumnGroup {
  /** Index of a column, where rows should be merged */
  columnIndex: number;
  /** Indices of columns that also need to take into comparison (from attribute relations) */
  relatedColumnIndices: number[];
}

/**
 * Groups cells over rows if its values are equal.
 *
 * @example
 * Source table data:
 *   col0 col1 col2 col3
 *      1    1    1    a
 *      1    1    1    b
 *      1    1    2    c
 *      1    2    1    d
 *      2    2    1    e
 *      2    3    1    f
 *
 * Column groups: [
 *                  {
 *                    columnIndex: 0,
 *                    relatedColumnIndices: []
 *                  },
 *                  {
 *                    columnIndex: 1,
 *                    relatedColumnIndices: [0]
 *                  },
 *                  {
 *                    columnIndex: 2,
 *                    relatedColumnIndices: [0, 1]
 *                  }
 *                ]
 *
 * Result table data:
 *   col0 col1 col2 col3
 *      1    1    1    a
 *                     b
 *                2    c
 *           2    1    d
 *      2    2    1    e
 *           3    1    f
 */
export class RowSpanOlapPivotTablePlugin extends OlapPivotTablePlugin {

  #columnGroups?: ColumnGroup[]; // Initialized with table configuration

  #stickyEvents: EventKey[] = [];

  override init () {
    on(this.table, AFTER_LOAD_DATA, () => this.#onAfterLoadData());
    on(this.table, DATATABLE_DESTROYED, () => this.#onDatatableDestroyed());
  }

  override configureWithConfigurationData (columnConfiguration: TableConfigurationDto) {
    this.#columnGroups = prepareRowSpanColumnGroups(this.table, columnConfiguration);
  }

  #onAfterLoadData () {
    // This means we got a brand-new scroll body, whose cells we observed, so we can clear our listeners as well
    this.#clearStickyCellListeners();

    // New body means new cells, let's merge them!
    this._mergeCells();
  }

  #onDatatableDestroyed () {
    // We lost the scroll body, whose cells we observed, so we can clear our listeners as well
    this.#clearStickyCellListeners();
  }

  _mergeCells () {
    if (!isSafari) {
      this._recalculateBodyMarginTop();
    }
    this.#columnGroups!.forEach(columnGroup => this._mergeColumn(columnGroup));

    if (!isSafari) {
      this.#initVerticallyStickyCellLabels();
    }
  }

  _mergeColumn (columnGroup: ColumnGroup) {
    const datatablesTableApi = this.table.getDataTable()!.table();

    const columnIndex = columnGroup.columnIndex;
    const columnIndicesToCompare = [ columnIndex, ...columnGroup.relatedColumnIndices ];
    const column = datatablesTableApi.column(columnIndex);
    const columnNodes = column.nodes();

    const tableData = datatablesTableApi.data();
    const rowLength = datatablesTableApi.rows()[0].length;
    let lastDataRow = tableData[0];
    let lastSequenceStartRowIndex = 0;

    for (let rowIndex = 0; rowIndex < rowLength; ++rowIndex) {
      const dataRow = tableData[rowIndex];
      const mergeWithCellOfPreviousRow = rowIndex > 0
        && columnIndicesToCompare.every(index => CELL_VALUE_COMPARE_FUNCTION(lastDataRow, dataRow, index));
      const node = columnNodes[rowIndex];

      // We need these classes for disabling odd/even background-color for all columns that contain
      // row groups.
      node.classList.add('d-table--row-span-group-column-cell');

      if (mergeWithCellOfPreviousRow) {
        node.hidden = true;
      } else {
        this.#applyRowSpan(columnNodes, lastSequenceStartRowIndex, rowIndex);
        lastSequenceStartRowIndex = rowIndex;
      }

      const isFirstRowOfGroup = rowIndex === lastSequenceStartRowIndex;
      if (isFirstRowOfGroup) {
        // Mark each cell vertically ending a group, including single row groups, for putting
        // emphasis on groups via more dominantly styled separator lines (border-top).
        const previousRow = node.parentNode.previousElementSibling;
        previousRow?.classList.add('d-table--vertically-visually-last-row-of-row-span-group');
      }

      lastDataRow = dataRow;
    }

    this.#applyRowSpan(columnNodes, lastSequenceStartRowIndex, rowLength);
  }

  #applyRowSpan (
    columnNodes: Api<Node[]>,
    lastSequenceStartRowIndex: number,
    rowIndex: number) {
    const rowSpan = rowIndex - lastSequenceStartRowIndex;
    if (rowSpan > 1) {
      const sequenceStartCellNode = columnNodes[lastSequenceStartRowIndex];
      this.#applySpanProperties(sequenceStartCellNode, rowSpan);
    }
  }

  // Careful overusing the sticky content element, it's what it was making the page very slow (even table unrelated parts like the navbar) because the html becomes too complex
  #applySpanProperties (cell: HTMLTableCellElement, rowSpan: number) {
    const newDiv = h('div.sticky-content.ellipsis', [ ...cell.childNodes ]);
    cell.append(newDiv);
    cell.hidden = false;
    cell.rowSpan = rowSpan;

    const tr = cell.parentElement as HTMLTableRowElement;
    const body = tr.parentElement as HTMLTableSectionElement;
    const allTrs = [ ...body.children ];
    const rowIndexInBody = allTrs.indexOf(tr);
    const isVerticallyLastGroup = allTrs.length === rowIndexInBody + rowSpan;

    cell.classList.toggle('d-table--row-span-group-cell-and-part-of-last-row', isVerticallyLastGroup);
    cell.classList.toggle(
      'd-table--row-span-group-cell-and-preceding-cell-of-subtotal-row',
      !isVerticallyLastGroup && (allTrs[rowIndexInBody + rowSpan] as BaseTableRowElement).dData.rowType === 'SUBTOTAL');
  }

  _recalculateBodyMarginTop () {
    // Same logic as in result-table::onFirstTableDraw
    const scrollBody = this.table.getScrollBody();
    if (!scrollBody) { // print mode -> scroller disabled
      return;
    }
    const dataTableElement = getDataTableElement(scrollBody);
    setDataTableMarginTop(this.table.getScrollHead()!, dataTableElement);
  }

  #initVerticallyStickyCellLabels () {
    const scrollBody = this.table.getScrollBody();
    if (!scrollBody) { // In case of reports, we do not have a scroll body
      return;
    }

    const scrollHead = this.table.getScrollHead()!;

    // Types of datatables and its plugins are so fucked up that it is not possible to have the correct type here
    const isFixedColumnsMode = !!this.table.getTableOptions().fixedColumns;

    const chromeBasedTopHeight = scrollHead.getBoundingClientRect().top
      + scrollHead.offsetHeight
      + parseFloat(getComputedStyle(scrollHead).marginBottom || '0');

    const updateTop = (cell: HTMLTableCellElement) => {
      const cellContent = cell.firstElementChild as HTMLElement | null;
      if (cellContent) {
        cellContent.style.top = calculateLabelTopValue(cell, scrollHead, isFixedColumnsMode, chromeBasedTopHeight);
      }
    };

    const stickyCells = new Set<HTMLTableCellElement>();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(({ target, isIntersecting }) => {
        const cell = target as HTMLTableCellElement;
        if (isIntersecting) {
          stickyCells.add(cell);
        } else {
          stickyCells.delete(cell);
          updateTop(cell);
        }
      });
    });
    this.#stickyEvents.push(
      on(scrollBody, 'scroll', () => {
        stickyCells.forEach(updateTop);
      })
    );

    const observedCells = scrollBody.querySelectorAll<HTMLTableCellElement>('td[rowspan]');
    [ ...observedCells ].forEach(cell => observer.observe(cell));
    observedCells.forEach((cell) => updateTop(cell));
  }

  #clearStickyCellListeners () {
    unByKey(this.#stickyEvents);
    this.#stickyEvents = [];
  }

}

customElements.define(COMPONENT_NAME, RowSpanOlapPivotTablePlugin);

function calculateLabelTopValue (cell: HTMLTableCellElement, scrollHead: HTMLElement, isFixedColumnsMode: boolean, chromeBasedTopHeight: number) {
  const rect = cell.getBoundingClientRect();
  if (isChromiumBrowser) {
    return px(Math.max(0, chromeBasedTopHeight - rect.top));
  }
  return isFixedColumnsMode ? '0px' : px(scrollHead.offsetHeight);
}

function prepareRowSpanColumnGroups (
  olapPivotTable: OlapPivotTable,
  { columnAttributeGroups }: TableConfigurationDto) {
  return columnAttributeGroups
    .map(columnAttributeGroup =>
      ({
        columnIndex: olapPivotTable.getColumnIndexByColumnId(columnAttributeGroup.columnId),
        relatedColumnIndices: columnAttributeGroup.relatedColumnIds
          .map(columnId => olapPivotTable.getColumnIndexByColumnId(columnId))
      } satisfies ColumnGroup))
    // We ignore the index column
    .filter(({ columnIndex }) => columnIndex !== 0)
    // Sort for easier debugging and marking the last column's cells. Couldn't figure out why it isn't sorted already.
    .sort((a, b) => a.columnIndex - b.columnIndex);
}
